import React, { useState } from 'react';
import {
  describeSentRequest,
  describeSystemRequest,
  SandboxExchange,
  toCurl,
} from '@/services/api/sandbox';
import { JsonBlock } from './sandboxUi';

type RequestView = 'page' | 'system';

const REQUEST_VIEWS: { key: RequestView; label: string; hint: string }[] = [
  {
    key: 'page',
    label: 'Sent by this page',
    hint: 'Your portal login, in sandbox mode.',
  },
  {
    key: 'system',
    label: 'From your system',
    hint: 'Your Sandbox key; no X-Api-Mode — the key decides the environment.',
  },
];

/** The simulator's own call: a portal tool, not part of the partner API. */
const isSimulation = (exchange: SandboxExchange) =>
  /\/simulate$/.test(exchange.path);

/**
 * The last API call this page made, read two ways: what the page really sent
 * (portal login + X-Api-Mode) and how the partner's own system makes the same
 * call (its dash_test_ key, shown by prefix), plus the response as returned.
 * A simulation step has no partner version, so it shows only what the page
 * sent — no "From your system" view, no cURL.
 */
const RequestResponsePanel: React.FC<{
  exchange: SandboxExchange | null;
  keyPrefix: string | null;
}> = ({ exchange, keyPrefix }) => {
  const [view, setView] = useState<RequestView>('page');
  const [copied, setCopied] = useState(false);

  if (!exchange) {
    return (
      <p className="py-6 text-center text-sm text-text-sub-600">
        Loading the request…
      </p>
    );
  }

  const copyCurl = async () => {
    try {
      await navigator.clipboard.writeText(toCurl(exchange));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be unavailable (permissions) — the request stays readable.
    }
  };

  const ok = exchange.status < 400;
  const internal = isSimulation(exchange);
  const views = internal
    ? REQUEST_VIEWS.filter((v) => v.key === 'page')
    : REQUEST_VIEWS;
  const shown: RequestView = internal ? 'page' : view;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div
            role="tablist"
            aria-label="Request view"
            className="inline-flex rounded-lg border border-stroke-soft-200 p-0.5"
          >
            {views.map((v) => (
              <button
                key={v.key}
                type="button"
                role="tab"
                aria-selected={shown === v.key}
                onClick={() => setView(v.key)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  shown === v.key
                    ? 'bg-(--primary-alpha-10) text-primary'
                    : 'text-text-sub-600 hover:text-text-strong-950'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          {shown === 'system' && (
            <button
              type="button"
              onClick={copyCurl}
              className="text-xs font-medium text-primary hover:underline"
            >
              <i className="ri-terminal-box-line mr-1" />
              {copied ? 'Copied' : 'Copy as cURL'}
            </button>
          )}
        </div>
        <p className="text-xs text-text-sub-600">
          {internal
            ? 'Your portal login, in sandbox mode. Simulation is a portal tool — your system never calls it.'
            : REQUEST_VIEWS.find((v) => v.key === shown)?.hint}
        </p>
        <JsonBlock
          label="Request"
          value={
            shown === 'page'
              ? describeSentRequest(exchange)
              : describeSystemRequest(exchange, keyPrefix)
          }
        />
      </div>
      <div className="space-y-2">
        <div className="flex h-[30px] items-center">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-mono text-xs font-semibold ${
              ok
                ? 'bg-success-lighter text-success-dark'
                : 'bg-error-lighter text-error-dark'
            }`}
          >
            HTTP {exchange.status}
          </span>
        </div>
        <p className="text-xs text-text-sub-600">
          {ok
            ? 'Response'
            : 'Error response — the HTTP status is the machine-readable part.'}
        </p>
        <JsonBlock label="Response" value={exchange.response} />
      </div>
    </div>
  );
};

export default RequestResponsePanel;
